import React from 'react';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import isArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import * as FileUtil from 'folder-items/FileUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {ArchiveModalItem, ArchiveOptionsForModal} from '../modals/ArchiveModal';
import ArchiveButton from './ArchiveButton';
import UnarchiveButton from './UnarchiveButton';

interface ArchiveButtonProps {
  className?: string;
  fullWidth: boolean;
  showText: boolean;
  buttonStyle: ButtonStyle;
  items: Array<ArchiveModalItem>;
  onArchive: (itemIds: Array<string>, purpose: string) => void;
  tooltipTitle?: string;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
}

interface ArchiveOrUnarchiveButtonProps {
  archiveButtonClass: React.ComponentType<ArchiveButtonProps>;
  archiveTooltipTitle?: string;
  unarchiveTooltipTitle?: string;
  buttonStyle?: ButtonStyle;
  fullWidth: boolean;
  className?: string;
  isArchivable: boolean;
  isUnarchivable: boolean;
  item: ArchiveModalItem;
  onArchive: (itemIds: Array<string>, purpose: string, options?: ArchiveOptionsForModal) => void;
  onUnarchive: (itemIds: Array<string>) => void;
  showText: boolean;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
}

export default function ArchiveOrUnarchiveButton(props: ArchiveOrUnarchiveButtonProps): JSX.Element | null {
  const sharedButtonProps = {
    className: props.className,
    fullWidth: props.fullWidth,
    showText: props.showText,
    getAnalyticsMetadata: props.getAnalyticsMetadata,
  };
  if (isArchived(props.item) && props.isUnarchivable) {
    return (
      <UnarchiveButton
        {...sharedButtonProps}
        buttonStyle={props.buttonStyle || ButtonStyle.SECONDARY}
        itemIds={[props.item.id]}
        onUnarchive={props.onUnarchive}
        unarchiveOptions={
          APIUtil.isBiologicalEntity(props.item.id)
            ? {
                hasRegisteredItems: props.item.isRegistered || FileUtil.isInRegistry(props.item),
              }
            : undefined
        }
        tooltipTitle={props.unarchiveTooltipTitle}
      />
    );
  }
  if (!isArchived(props.item) && isArchivable(props.item) && props.isArchivable) {
    const ArchiveButtonClass = props.archiveButtonClass;
    return (
      <ArchiveButtonClass
        {...sharedButtonProps}
        buttonStyle={props.buttonStyle || ButtonStyle.NEGATIVE}
        items={[props.item]}
        onArchive={props.onArchive}
        tooltipTitle={props.archiveTooltipTitle}
      />
    );
  }
  return null;
}

ArchiveOrUnarchiveButton.defaultProps = {
  archiveButtonClass: ArchiveButton,
  fullWidth: false,
  isArchivable: false,
  isUnarchivable: false,
  showText: false,
};
